import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LaunchIcon from "@mui/icons-material/Launch";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { EMBED_BOTS, LOADER_TEMPLATE, embedLiveUrl } from "../../utils/embedScripts";
import {
  publishScripts,
  restoreBackup,
  verifyLiveScripts,
} from "../../utils/embedPublisher";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import Widget from "../ui/widget";
import WidgetTitle from "../ui/widgetTitle";

const STATUS_LABELS = {
  checking: "Checking…",
  "up-to-date": "Up To Date",
  "needs-update": "Needs Update",
  missing: "Not Published",
  error: "Error",
};

const InjectionScripts = props => {
  const { widgetSize = "large", minHeight = "333px" } = props;

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [verifyResults, setVerifyResults] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const [confirmBots, setConfirmBots] = useState(null);

  const runVerification = useCallback(async () => {
    setIsVerifying(true);
    const checking = {};
    EMBED_BOTS.forEach(bot => {
      checking[bot.key] = { key: bot.key, status: "checking" };
    });
    setVerifyResults(checking);

    try {
      const results = await verifyLiveScripts(EMBED_BOTS, (key, result) => {
        setVerifyResults(previous => ({ ...previous, [key]: result }));
      });
      setVerifyResults(results);
    } finally {
      setIsVerifying(false);
    }
  }, []);

  useEffect(() => {
    runVerification();
  }, [runVerification]);

  const statusSx = useCallback(
    status => {
      switch (status) {
        case "up-to-date":
          return {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
          };
        case "needs-update":
          return {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          };
        case "error":
          return {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
          };
        case "missing":
          return {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.info.contrastText,
          };
        default:
          return {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.text.secondary,
          };
      }
    },
    [theme],
  );

  const rows = useMemo(
    () =>
      EMBED_BOTS.map(bot => {
        const result = verifyResults[bot.key] || { status: "checking" };
        return {
          key: bot.key,
          bot,
          displayName: bot.displayName,
          expectedVersion: result.expectedVersion || bot.botpressVersion || "v3.6",
          liveVersion: result.liveVersion || "—",
          status: result.status || "checking",
          liveUrl: embedLiveUrl(bot),
        };
      }),
    [verifyResults],
  );

  const outOfDateCount = rows.filter(
    row => row.status === "needs-update" || row.status === "missing",
  ).length;

  const handleUndo = useCallback(
    async (backupFolder, bots) => {
      try {
        await restoreBackup(backupFolder, bots);
        enqueueSnackbar("Reverted to the previous version.", { variant: "success" });
        runVerification();
      } catch (error) {
        enqueueSnackbar(`Undo failed: ${error.message}`, { variant: "error" });
      }
    },
    [enqueueSnackbar, runVerification],
  );

  const handlePublish = useCallback(
    async bots => {
      setIsPublishing(true);
      try {
        const outcome = await publishScripts(bots);
        setConfirmBots(null);
        const count = `${bots.length} script${bots.length === 1 ? "" : "s"}`;
        if (outcome.backupFolder) {
          enqueueSnackbar(
            `Published ${count} — previous version${bots.length === 1 ? "" : "s"} backed up to ${outcome.backupFolder}.`,
            {
              variant: "success",
              autoHideDuration: 12000,
              action: () => (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => handleUndo(outcome.backupFolder, bots)}
                >
                  Undo
                </Button>
              ),
            },
          );
        } else {
          enqueueSnackbar(
            `Published ${count}, but the server did not confirm a backup. Verify a backup exists before relying on undo.`,
            { variant: "warning", autoHideDuration: 15000 },
          );
        }
        runVerification();
      } catch (error) {
        enqueueSnackbar(`Update failed: ${error.message}`, { variant: "error" });
      } finally {
        setIsPublishing(false);
      }
    },
    [enqueueSnackbar, handleUndo, runVerification],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "displayName",
        header: "Client Bot",
        size: 260,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {row.original.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.key}.js
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "expectedVersion",
        header: "Version",
        size: 130,
        Cell: ({ row }) => {
          const { expectedVersion, liveVersion, status } = row.original;
          const drift =
            status === "needs-update" && liveVersion && liveVersion !== expectedVersion;
          return (
            <Box>
              <Typography variant="body2">{expectedVersion}</Typography>
              {drift && (
                <Typography variant="caption" color="warning.main">
                  live: {liveVersion}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        Cell: ({ cell }) => {
          const status = cell.getValue();
          return (
            <Box
              component="span"
              sx={{
                ...statusSx(status),
                borderRadius: "0.25rem",
                display: "inline-block",
                minWidth: "110px",
                textAlign: "center",
                padding: theme.spacing(0.75),
              }}
            >
              <Typography variant="button" sx={{ fontWeight: "bold", fontSize: "0.72rem" }}>
                {STATUS_LABELS[status] || status}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 130,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Regenerate & publish this file" arrow>
              <span>
                <IconButton
                  size="small"
                  color="secondary"
                  disabled={isPublishing || isVerifying}
                  onClick={() => setConfirmBots([row.original.bot])}
                >
                  <CloudUploadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Open the live file" arrow>
              <IconButton
                size="small"
                onClick={() => window.open(row.original.liveUrl, "_blank")}
              >
                <LaunchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [isPublishing, isVerifying, statusSx, theme],
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enablePagination: false,
    enableSorting: true,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableStickyHeader: true,
    layoutMode: "grid",
    initialState: { density: "compact" },
    state: { isLoading: isVerifying && Object.keys(verifyResults).length === 0 },
    getRowId: row => row.key,
    muiTablePaperProps: { sx: { boxShadow: "none", display: "flex", flexDirection: "column" } },
    muiTableContainerProps: { sx: { flex: 1, minHeight: 0, overflowY: "auto" } },
  });

  const isSingle = confirmBots && confirmBots.length === 1;
  const confirmTitle = isSingle
    ? `Update “${confirmBots[0].displayName}”`
    : `Update all ${EMBED_BOTS.length} injection scripts`;

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title="Injection Scripts"
        text={
          isVerifying
            ? "Verifying against live…"
            : `${EMBED_BOTS.length} scripts · ${outOfDateCount} need updating`
        }
        loading={isVerifying && Object.keys(verifyResults).length === 0}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Re-verify against live" arrow>
            <span>
              <IconButton onClick={runVerification} disabled={isVerifying || isPublishing}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <OmniResponsiveButton
            tooltipTitle="Regenerate & publish every file"
            buttonText="Update All"
            icon={<CloudUploadIcon />}
            disabled={isPublishing || isVerifying}
            onClick={() => setConfirmBots(EMBED_BOTS)}
          />
        </Box>
      </WidgetTitle>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 1 }}>
        <MaterialReactTable table={table} />
      </Box>

      <Dialog
        open={confirmBots != null}
        onClose={() => !isPublishing && setConfirmBots(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            This regenerates {isSingle ? "this file" : "every file"} from the hardcoded template
            below and overwrites {isSingle ? "it" : "them"} in S3, then invalidates CloudFront.
            The current live file{isSingle ? "" : "s"} will be copied to a timestamped{" "}
            <code>backups/</code> folder first, and this update can be undone.
          </DialogContentText>

          <Alert severity="warning" sx={{ mb: 2 }}>
            A bad file is a client-facing point of failure. Review the template before confirming.
          </Alert>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Files to update ({isSingle ? 1 : EMBED_BOTS.length})
          </Typography>
          <Box
            sx={{
              maxHeight: 120,
              overflowY: "auto",
              mb: 2,
              p: 1,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }}
          >
            {(confirmBots || []).map(bot => (
              <Typography key={bot.key} variant="body2">
                {bot.key}.js — {bot.displayName}
              </Typography>
            ))}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Config template
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              maxHeight: 260,
              overflow: "auto",
              backgroundColor: theme.palette.grey[900],
              color: theme.palette.grey[100],
              borderRadius: 1,
              fontSize: "0.72rem",
              lineHeight: 1.4,
              whiteSpace: "pre",
            }}
          >
            {LOADER_TEMPLATE}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBots(null)} disabled={isPublishing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={isPublishing}
            onClick={() => handlePublish(confirmBots)}
          >
            {isPublishing ? "Publishing…" : isSingle ? "Update file" : "Update all files"}
          </Button>
        </DialogActions>
      </Dialog>
    </Widget>
  );
};

export default InjectionScripts;
